import { useState, useCallback, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faPlus,
  faTrash,
  faCheck,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { useBIFields } from '../hooks/useBIFields';
import ProjectionConfigEditor from './ProjectionConfigEditor';
import AnnotationEditor from './AnnotationEditor';
import ConditionalFormatRules from './ConditionalFormatRules';
import DrilldownConfigEditor from './DrilldownConfigEditor';
import type {
  BIQueryError,
  BIQueryErrorDetail,
  WidgetType,
  DataConfig,
  DateGranularity,
  Measure,
  FilterCondition,
  SortConfig,
  ProjectionConfig,
  Annotation,
  ConditionalFormatRule,
  DrilldownConfig,
  SemanticIntent,
  StyleConfig,
} from '../../../types/bi';
import styles from './QueryDesigner.module.css';
import { getWidgetCapabilities } from '../utils/widgetCapabilities';
import type { CuratedMeasure } from '../hooks/useBIFields';
import { buildSemanticIntentFromDataConfig } from '../utils/semanticIntent';
import { applyMeasureAliasPolicy, type AliasPolicyMode } from '../utils/aliasPolicy';

const AGGREGATIONS: Measure['aggregation'][] = [
  'sum',
  'avg',
  'count',
  'count_distinct',
  'min',
  'max',
];

function isSupportedAggregation(value: string | undefined): value is Measure['aggregation'] {
  return !!value && AGGREGATIONS.includes(value as Measure['aggregation']);
}

const FILTER_OPERATORS: { value: FilterCondition['operator']; label: string }[] = [
  { value: '=', label: 'equals' },
  { value: '!=', label: 'not equals' },
  { value: '>', label: 'greater than' },
  { value: '<', label: 'less than' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: 'in', label: 'in' },
  { value: 'not_in', label: 'not in' },
  { value: 'between', label: 'between' },
  { value: 'like', label: 'contains' },
  { value: 'is_null', label: 'is null' },
  { value: 'is_not_null', label: 'is not null' },
];

const AGGREGATE_FILTER_OPERATORS: { value: FilterCondition['operator']; label: string }[] = [
  { value: '=', label: 'equals' },
  { value: '!=', label: 'not equals' },
  { value: '>', label: 'greater than' },
  { value: '<', label: 'less than' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: 'between', label: 'between' },
];

const TEXT_FILTER_OPERATORS: { value: FilterCondition['operator']; label: string }[] = [
  { value: '=', label: 'equals' },
  { value: '!=', label: 'not equals' },
  { value: 'in', label: 'in' },
  { value: 'not_in', label: 'not in' },
  { value: 'like', label: 'contains' },
  { value: 'is_null', label: 'is null' },
  { value: 'is_not_null', label: 'is not null' },
];

const NUMERIC_DATE_FILTER_OPERATORS: { value: FilterCondition['operator']; label: string }[] = [
  { value: '=', label: 'equals' },
  { value: '!=', label: 'not equals' },
  { value: '>', label: 'greater than' },
  { value: '<', label: 'less than' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: 'in', label: 'in' },
  { value: 'not_in', label: 'not in' },
  { value: 'between', label: 'between' },
  { value: 'is_null', label: 'is null' },
  { value: 'is_not_null', label: 'is not null' },
];

const OPTION_FILTER_OPERATORS: { value: FilterCondition['operator']; label: string }[] = [
  { value: '=', label: 'equals' },
  { value: '!=', label: 'not equals' },
  { value: 'is_null', label: 'is null' },
  { value: 'is_not_null', label: 'is not null' },
];

const FILTER_SCOPE_OPTIONS: { value: NonNullable<FilterCondition['scope']>; label: string }[] = [
  { value: 'value', label: 'Value Of' },
  { value: 'count_of', label: 'Count Of' },
  { value: 'sum_of', label: 'Sum Of' },
  { value: 'avg_of', label: 'Average Of' },
];

type FilterValueKind = 'text' | 'number' | 'date' | 'option';
type FilterScope = NonNullable<FilterCondition['scope']>;
type TemporalAxisMode = 'category' | 'time';

const NUMERIC_TYPES = new Set([
  'number',
  'numeric',
  'integer',
  'float',
  'decimal',
  'currency',
  'percentage',
]);
const DATE_TYPES = new Set(['date', 'datetime', 'timestamp', 'timestamptz']);
const OPTION_TYPES = new Set(['dropdown', 'multiple', 'select', 'multi_select']);
const DATE_FIELD_TYPES = new Set(['date', 'datetime', 'timestamp', 'timestamptz']);
const DATE_DB_TYPES = new Set(['date', 'timestamp', 'timestamptz', 'datetime']);
const TIME_GRANULARITY_OPTIONS: DateGranularity[] = ['day', 'week', 'month', 'quarter', 'year'];

function isDateLikeField(fieldType?: string, dbType?: string): boolean {
  const field = (fieldType || '').toLowerCase();
  const db = (dbType || '').toLowerCase();
  return DATE_FIELD_TYPES.has(field) || DATE_DB_TYPES.has(db);
}

function resolveFilterValueKind(fieldType?: string, dbType?: string): FilterValueKind {
  const field = (fieldType || '').toLowerCase();
  const db = (dbType || '').toLowerCase();

  if (OPTION_TYPES.has(field) || OPTION_TYPES.has(db)) return 'option';
  if (NUMERIC_TYPES.has(field) || NUMERIC_TYPES.has(db)) return 'number';
  if (DATE_TYPES.has(field) || DATE_TYPES.has(db)) return 'date';
  return 'text';
}

function parseScalarFilterValue(raw: string, kind: FilterValueKind): unknown {
  if (kind === 'number') {
    if (!raw.trim()) return '';
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : '';
  }

  return raw;
}

function resolveFilterScope(scope: FilterCondition['scope']): FilterScope {
  if (scope === 'count_of' || scope === 'sum_of' || scope === 'avg_of') return scope;
  return 'value';
}

function isAggregateFilterScope(scope: FilterScope): boolean {
  return scope === 'count_of' || scope === 'sum_of' || scope === 'avg_of';
}

function getFilterOperatorsForScope(scope: FilterScope) {
  return isAggregateFilterScope(scope) ? AGGREGATE_FILTER_OPERATORS : FILTER_OPERATORS;
}

function getTypedFilterOperators(kind: FilterValueKind) {
  if (kind === 'number' || kind === 'date') return NUMERIC_DATE_FILTER_OPERATORS;
  if (kind === 'option') return OPTION_FILTER_OPERATORS;
  return TEXT_FILTER_OPERATORS;
}

type VisualSlotKind = 'dimension' | 'measure';

interface VisualSlotDefinition {
  key: 'category' | 'series' | 'value' | 'x_value' | 'y_value';
  label: string;
  kind: VisualSlotKind;
  optional?: boolean;
  helperText?: string;
  hintText?: string;
}

interface VisualSlotConstraints {
  maxDimensions: number;
  maxMeasures: number;
}

function getVisualSlotDefinitions(widgetType?: WidgetType): VisualSlotDefinition[] {
  if (widgetType === 'kpi' || widgetType === 'gauge') {
    return [
      {
        key: 'value',
        label: 'Value',
        kind: 'measure',
        hintText: 'Choose the primary metric to surface as the headline value.',
      },
    ];
  }

  if (widgetType === 'scatter') {
    return [
      {
        key: 'x_value',
        label: 'X value',
        kind: 'measure',
        hintText: 'Pick the metric for the horizontal axis.',
      },
      {
        key: 'y_value',
        label: 'Y value',
        kind: 'measure',
        hintText: 'Pick the metric for the vertical axis.',
      },
    ];
  }

  if (
    widgetType === 'pie' ||
    widgetType === 'donut' ||
    widgetType === 'funnel' ||
    widgetType === 'treemap'
  ) {
    return [
      {
        key: 'category',
        label: 'Category',
        kind: 'dimension',
        hintText: 'Choose the bucket field used to split each slice or stage.',
      },
      {
        key: 'value',
        label: 'Value',
        kind: 'measure',
        hintText: 'Choose the metric used to size each bucket.',
      },
    ];
  }

  if (
    widgetType === 'bar' ||
    widgetType === 'line' ||
    widgetType === 'area' ||
    widgetType === 'waterfall' ||
    widgetType === 'sparkline'
  ) {
    return [
      {
        key: 'category',
        label: 'Category axis',
        kind: 'dimension',
        hintText: 'Choose the field shown on the X axis or timeline.',
      },
      {
        key: 'series',
        label: 'Series split',
        kind: 'dimension',
        optional: true,
        helperText: 'Optional',
        hintText: 'Optional breakdown used to split lines or bars by subgroup.',
      },
      {
        key: 'value',
        label: 'Value',
        kind: 'measure',
        hintText: 'Choose the metric shown on the Y axis.',
      },
    ];
  }

  if (widgetType === 'data_table') {
    return [
      {
        key: 'category',
        label: 'Category column',
        kind: 'dimension',
        hintText: 'Choose the primary grouping column shown in the table.',
      },
      {
        key: 'value',
        label: 'Value column',
        kind: 'measure',
        optional: true,
        helperText: 'Optional',
        hintText: 'Optional metric column. Leave blank for dimension-only table views.',
      },
    ];
  }

  return [
    {
      key: 'category',
      label: 'Category',
      kind: 'dimension',
      hintText: 'Choose the grouping field users should slice by.',
    },
    {
      key: 'value',
      label: 'Value',
      kind: 'measure',
      hintText: 'Choose the metric to aggregate in the visual.',
    },
  ];
}

function getVisualSlotConstraints(widgetType?: WidgetType): VisualSlotConstraints {
  if (widgetType === 'kpi' || widgetType === 'gauge') {
    return { maxDimensions: 0, maxMeasures: 1 };
  }

  if (widgetType === 'scatter') {
    return { maxDimensions: 0, maxMeasures: 2 };
  }

  if (
    widgetType === 'pie' ||
    widgetType === 'donut' ||
    widgetType === 'funnel' ||
    widgetType === 'treemap'
  ) {
    return { maxDimensions: 1, maxMeasures: 1 };
  }

  if (
    widgetType === 'bar' ||
    widgetType === 'line' ||
    widgetType === 'area' ||
    widgetType === 'waterfall' ||
    widgetType === 'sparkline'
  ) {
    return { maxDimensions: 2, maxMeasures: 1 };
  }

  if (widgetType === 'data_table') {
    return { maxDimensions: 1, maxMeasures: 1 };
  }

  return { maxDimensions: 1, maxMeasures: 1 };
}

function normaliseFilterValue(
  operator: FilterCondition['operator'],
  currentValue: unknown,
  kind: FilterValueKind = 'text',
) {
  if (operator === 'between') {
    if (Array.isArray(currentValue)) {
      return [
        parseScalarFilterValue(String(currentValue[0] ?? ''), kind),
        parseScalarFilterValue(String(currentValue[1] ?? ''), kind),
      ];
    }

    return ['', ''];
  }

  if (operator === 'in' || operator === 'not_in') {
    if (Array.isArray(currentValue)) {
      return currentValue
        .map((value) => parseScalarFilterValue(String(value ?? ''), kind))
        .filter((value) => value !== '');
    }

    const text = String(currentValue ?? '').trim();
    return text
      ? text
          .split(',')
          .map((value) => parseScalarFilterValue(value.trim(), kind))
          .filter((value) => value !== '')
      : [];
  }

  if (Array.isArray(currentValue)) {
    return parseScalarFilterValue(String(currentValue[0] ?? ''), kind);
  }

  return parseScalarFilterValue(String(currentValue ?? ''), kind);
}

function formatDelimitedValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry ?? '')).join(', ');
  }

  return String(value ?? '');
}

export interface WidgetConfigUpdate {
  data_config: DataConfig;
  semantic_intent?: SemanticIntent | null;
  style_config: StyleConfig;
  projection_config: ProjectionConfig | null;
  annotations: Annotation[];
  conditional_formatting: ConditionalFormatRule[];
  drilldown_config: DrilldownConfig | null;
}

interface QueryDesignerProps {
  dataConfig: DataConfig;
  widgetType?: WidgetType;
  validationError?: BIQueryError | null;
  styleConfig?: StyleConfig;
  projectionConfig?: ProjectionConfig | null;
  annotations?: Annotation[];
  conditionalFormatting?: ConditionalFormatRule[];
  drilldownConfig?: DrilldownConfig | null;
  onSave: (update: WidgetConfigUpdate) => void;
  onClose: () => void;
}

export default function QueryDesigner({
  dataConfig,
  widgetType,
  validationError,
  styleConfig: initialStyle = {},
  projectionConfig: initialProjection = null,
  annotations: initialAnnotations = [],
  conditionalFormatting: initialFormatting = [],
  drilldownConfig: initialDrilldown = null,
  onSave,
  onClose,
}: QueryDesignerProps) {
  const { data: fieldsData, isLoading } = useBIFields();
  const [config, setConfig] = useState<DataConfig>(() => ({
    source_table: dataConfig.source_table || '',
    dimensions: [...(dataConfig.dimensions || [])],
    measures: (dataConfig.measures || []).map((m: Measure) => ({ ...m })),
    filters: (dataConfig.filters || []).map((f: FilterCondition) => ({ ...f })),
    sort: (dataConfig.sort || []).map((s: SortConfig) => ({ ...s })),
    limit: dataConfig.limit || 1000,
    comparative_date: dataConfig.comparative_date || undefined,
    date_granularity: dataConfig.date_granularity ? { ...dataConfig.date_granularity } : undefined,
  }));

  const [projectionCfg, setProjectionCfg] = useState<ProjectionConfig | null>(initialProjection);
  const [annotationsList, setAnnotationsList] = useState<Annotation[]>(() =>
    initialAnnotations.map((a) => ({ ...a })),
  );
  const [formatRules, setFormatRules] = useState<ConditionalFormatRule[]>(() =>
    initialFormatting.map((r) => ({ ...r })),
  );
  const [drilldownCfg, setDrilldownCfg] = useState<DrilldownConfig | null>(initialDrilldown);
  const [styleCfg, setStyleCfg] = useState<StyleConfig>(() => ({ ...initialStyle }));
  const [advancedModeEnabled, setAdvancedModeEnabled] = useState(false);
  const [aliasPolicyMode, setAliasPolicyMode] = useState<AliasPolicyMode>('auto');
  const [slotSearchValues, setSlotSearchValues] = useState<
    Partial<Record<VisualSlotDefinition['key'], string>>
  >({});
  const [measureSearchValues, setMeasureSearchValues] = useState<Record<number, string>>({});
  const [filterFieldSearchValues, setFilterFieldSearchValues] = useState<Record<number, string>>(
    {},
  );
  const [temporalAxisMode, setTemporalAxisMode] = useState<TemporalAxisMode>(() => {
    const firstDimension = dataConfig.dimensions?.[0] || '';
    return firstDimension && dataConfig.date_granularity?.[firstDimension] ? 'time' : 'category';
  });
  const [temporalGranularity, setTemporalGranularity] = useState<DateGranularity>(() => {
    const firstDimension = dataConfig.dimensions?.[0] || '';
    return (
      (dataConfig.date_granularity?.[firstDimension] as DateGranularity | undefined) || 'month'
    );
  });

  const {
    supportsProjection,
    supportsAnnotations,
    supportsLegend,
    supportsTooltip,
    supportsDataLabels,
    supportsGridLines,
    supportsDataZoom,
    supportsAxisLabels,
    supportsTemporalAxis,
  } = getWidgetCapabilities(widgetType);

  const guidanceHints: string[] = [];
  const measureCount = config.measures.filter((m) => m.field_key).length;
  const dimensionCount = config.dimensions.filter(Boolean).length;

  if ((widgetType === 'kpi' || widgetType === 'gauge') && measureCount !== 1) {
    guidanceHints.push('This visual requires exactly one measure.');
  }
  if (widgetType === 'scatter' && measureCount < 2) {
    guidanceHints.push('Scatter requires at least two measures (X and Y values).');
  }
  if ((widgetType === 'pie' || widgetType === 'donut') && dimensionCount < 1) {
    guidanceHints.push('Pie and donut visuals require at least one category field.');
  }
  if ((widgetType === 'line' || widgetType === 'area') && dimensionCount < 1) {
    guidanceHints.push('Line and area visuals require at least one category field on the axis.');
  }
  if (supportsProjection && projectionCfg?.type && dimensionCount < 1) {
    guidanceHints.push('Projection requires a category field so a time axis can be resolved.');
  }

  const apiDetails: BIQueryErrorDetail[] = Array.isArray(validationError?.details)
    ? validationError.details
    : [];
  const matchesPath = (detail: BIQueryErrorDetail, paths: string[]) =>
    !!detail.path && paths.some((path) => detail.path?.startsWith(path));
  const getIndexedIssues = (basePath: string, index: number) =>
    apiDetails.filter(
      (detail) =>
        !!detail.path &&
        (detail.path === `${basePath}[${index}]` ||
          detail.path.startsWith(`${basePath}[${index}].`)),
    );

  const dimensionIssues = apiDetails.filter((d) =>
    matchesPath(d, ['roles.category_fields', 'roles.series_fields', 'roles.label_fields']),
  );
  const measureIssues = apiDetails.filter((d) => matchesPath(d, ['roles.value_fields']));
  const filterIssues = apiDetails.filter((d) => matchesPath(d, ['filters']));
  const sortIssues = apiDetails.filter((d) => matchesPath(d, ['sorting']));
  const projectionIssues = apiDetails.filter((d) =>
    matchesPath(d, ['bindings', 'roles.category_fields']),
  );

  // All fields are now exposed together — the OLAP engine resolves the
  // appropriate base table and joins automatically (everything ultimately
  // reads from olap.case_fields + dim_* tables). The legacy “Source Table”
  // picker that used to filter the field list has been removed.
  const availableFields =
    fieldsData?.categories.flatMap(
      (c: {
        name: string;
        fields: {
          field_key: string;
          field_name: string;
          field_type: string;
          db_type?: string;
          source_table: string;
          hidden?: boolean;
          options?: { value: string; label: string }[];
        }[];
      }) =>
        c.fields.map(
          (f: {
            field_key: string;
            field_name: string;
            field_type: string;
            db_type?: string;
            source_table: string;
            hidden?: boolean;
            options?: { value: string; label: string }[];
          }) => ({ ...f, category: c.name }),
        ),
    ) || [];

  const semanticFieldByKey = useMemo(() => {
    const entries = fieldsData?.semantic_field_catalog || [];
    return new Map(entries.map((entry) => [entry.field_key, entry]));
  }, [fieldsData?.semantic_field_catalog]);

  const hasSemanticHints = semanticFieldByKey.size > 0;

  const dimensionFieldOptions = hasSemanticHints
    ? availableFields.filter(
        (field) =>
          !field.hidden &&
          semanticFieldByKey.get(field.field_key)?.role_compatibility?.includes('category'),
      )
    : availableFields.filter((field) => !field.hidden);

  const measureFieldOptions = hasSemanticHints
    ? availableFields.filter((field) => {
        if (field.hidden) return false;
        const semanticField = semanticFieldByKey.get(field.field_key);
        if (!semanticField) return true;

        const roles = semanticField.role_compatibility || [];
        return (
          roles.includes('value') ||
          roles.includes('category') ||
          roles.includes('series') ||
          roles.includes('label')
        );
      })
    : availableFields.filter((field) => !field.hidden);

  const curatedMeasures = fieldsData?.semantic_catalog?.curated_measures || [];
  const derivedDimensions = fieldsData?.semantic_catalog?.derived_fields || [];
  const catalogSummary = fieldsData?.semantic_catalog;
  const featuredMeasureKeys = catalogSummary?.featured_measure_keys || [];
  const featuredMeasures =
    featuredMeasureKeys.length > 0
      ? curatedMeasures.filter((measure) => featuredMeasureKeys.includes(measure.key))
      : [];
  const visualSlots = useMemo(() => getVisualSlotDefinitions(widgetType), [widgetType]);
  const slotConstraints = useMemo(() => getVisualSlotConstraints(widgetType), [widgetType]);

  // Sort field options: selected dimensions + measure aliases + hidden sort-support fields
  // whose source_table matches any currently selected dimension's source_table.
  const sortFieldOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];

    config.dimensions.forEach((dimKey) => {
      const fieldDef = availableFields.find((f) => f.field_key === dimKey);
      options.push({ value: dimKey, label: fieldDef?.field_name || dimKey });
    });

    config.measures.forEach((m) => {
      if (m.alias) options.push({ value: m.alias, label: m.alias });
    });

    // Collect source_tables of the selected dimensions so we can surface
    // sort-only hidden fields that are contextually relevant.
    const activeSourceTables = new Set(
      config.dimensions
        .map((dimKey) => availableFields.find((f) => f.field_key === dimKey)?.source_table)
        .filter((st): st is string => Boolean(st)),
    );

    if (activeSourceTables.size > 0) {
      availableFields
        .filter((f) => f.hidden && f.source_table && activeSourceTables.has(f.source_table))
        .forEach((f) => options.push({ value: f.field_key, label: f.field_name }));
    }

    return options;
  }, [config.dimensions, config.measures, availableFields]);

  const primaryDimension = config.dimensions[0] || '';
  const secondaryDimension = config.dimensions[1] || '';
  const primaryMeasure = config.measures[0]?.field_key || '';
  const secondaryMeasure = config.measures[1]?.field_key || '';

  const temporalDimensionOptions = useMemo(
    () => dimensionFieldOptions.filter((field) => isDateLikeField(field.field_type, field.db_type)),
    [dimensionFieldOptions],
  );

  useEffect(() => {
    if (!supportsTemporalAxis || temporalAxisMode !== 'time') return;

    const currentDimension = config.dimensions[0] || '';
    const fallbackTemporalFieldKey = temporalDimensionOptions[0]?.field_key || '';

    if (!currentDimension) {
      if (!fallbackTemporalFieldKey) return;

      setConfig((currentConfig) => {
        const existingSecondary = currentConfig.dimensions[1];
        const nextDimensions = [fallbackTemporalFieldKey, existingSecondary].filter(Boolean);
        const nextGranularityMap = { ...(currentConfig.date_granularity || {}) };
        nextGranularityMap[fallbackTemporalFieldKey] = temporalGranularity;

        return {
          ...currentConfig,
          dimensions: nextDimensions,
          date_granularity: nextGranularityMap,
        };
      });

      return;
    }

    const selectedField = availableFields.find((field) => field.field_key === currentDimension);
    if (selectedField && isDateLikeField(selectedField.field_type, selectedField.db_type)) {
      setConfig((currentConfig) => {
        const nextGranularityMap = { ...(currentConfig.date_granularity || {}) };
        if (nextGranularityMap[currentDimension] === temporalGranularity) {
          return currentConfig;
        }

        nextGranularityMap[currentDimension] = temporalGranularity;
        return {
          ...currentConfig,
          date_granularity: nextGranularityMap,
        };
      });
      return;
    }

    setConfig((currentConfig) => {
      if (!currentConfig.dimensions.length) return currentConfig;

      const [first, second] = currentConfig.dimensions;
      const nextGranularityMap = { ...(currentConfig.date_granularity || {}) };
      delete nextGranularityMap[first];

      if (fallbackTemporalFieldKey) {
        nextGranularityMap[fallbackTemporalFieldKey] = temporalGranularity;
        return {
          ...currentConfig,
          dimensions: [fallbackTemporalFieldKey, second].filter(Boolean),
          date_granularity: nextGranularityMap,
        };
      }

      return {
        ...currentConfig,
        dimensions: [],
        date_granularity: Object.keys(nextGranularityMap).length ? nextGranularityMap : undefined,
      };
    });
  }, [
    availableFields,
    config.dimensions,
    setConfig,
    supportsTemporalAxis,
    temporalAxisMode,
    temporalGranularity,
    temporalDimensionOptions,
  ]);

  const missingRequiredSlots = useMemo(
    () =>
      visualSlots
        .filter((slot) => !slot.optional)
        .filter((slot) => {
          if (slot.key === 'category') return !primaryDimension;
          if (slot.key === 'series') return !secondaryDimension;
          if (slot.key === 'value' || slot.key === 'x_value') return !primaryMeasure;
          if (slot.key === 'y_value') return !secondaryMeasure;
          return false;
        })
        .map((slot) => slot.label),
    [visualSlots, primaryDimension, secondaryDimension, primaryMeasure, secondaryMeasure],
  );

  const applySlotConstraints = useCallback(
    (nextConfig: DataConfig): DataConfig => {
      const trimmedMeasures = (nextConfig.measures || []).slice(0, slotConstraints.maxMeasures);
      return {
        ...nextConfig,
        dimensions: (nextConfig.dimensions || []).slice(0, slotConstraints.maxDimensions),
        measures: applyMeasureAliasPolicy(trimmedMeasures, aliasPolicyMode),
      };
    },
    [slotConstraints.maxDimensions, slotConstraints.maxMeasures, aliasPolicyMode],
  );

  const buildMeasure = useCallback(
    (fieldKey: string, fallbackIndex: number, existing?: Measure): Measure => {
      const semanticHint = semanticFieldByKey.get(fieldKey);
      const inferredAgg = isSupportedAggregation(semanticHint?.default_agg_hint)
        ? semanticHint.default_agg_hint
        : 'count';

      return {
        field_key: fieldKey,
        aggregation: existing?.aggregation || inferredAgg,
        alias: existing?.alias || `measure_${fallbackIndex}`,
      };
    },
    [semanticFieldByKey],
  );

  const handleSave = useCallback(() => {
    const normalizedConfig = {
      ...config,
      measures: applyMeasureAliasPolicy(config.measures || [], aliasPolicyMode),
    };
    const semanticIntent = widgetType
      ? buildSemanticIntentFromDataConfig(normalizedConfig, widgetType, projectionCfg)
      : undefined;

    onSave({
      data_config: normalizedConfig,
      semantic_intent: semanticIntent,
      style_config: styleCfg,
      projection_config: projectionCfg,
      annotations: annotationsList,
      conditional_formatting: formatRules,
      drilldown_config: drilldownCfg,
    });
  }, [
    aliasPolicyMode,
    annotationsList,
    config,
    drilldownCfg,
    formatRules,
    onSave,
    projectionCfg,
    styleCfg,
    widgetType,
  ]);

  /* ---------- Dimensions ---------- */
  const addDimension = useCallback(
    (fieldKey: string) => {
      if (config.dimensions.includes(fieldKey)) return;
      setConfig((c) => ({ ...c, dimensions: [...c.dimensions, fieldKey] }));
    },
    [config.dimensions],
  );
  const removeDimension = useCallback((idx: number) => {
    setConfig((c) => ({ ...c, dimensions: c.dimensions.filter((_, i) => i !== idx) }));
  }, []);

  /* ---------- Measures ---------- */
  const addMeasure = useCallback(() => {
    setConfig((c) => ({
      ...c,
      measures: applyMeasureAliasPolicy(
        [
          ...c.measures,
          { field_key: '', aggregation: 'count', alias: `measure_${c.measures.length + 1}` },
        ],
        aliasPolicyMode,
      ),
    }));
  }, [aliasPolicyMode]);
  const updateMeasure = useCallback(
    (idx: number, patch: Partial<Measure>) => {
      setConfig((c) => ({
        ...c,
        measures: applyMeasureAliasPolicy(
          c.measures.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
          aliasPolicyMode,
        ),
      }));
    },
    [aliasPolicyMode],
  );
  const removeMeasure = useCallback(
    (idx: number) => {
      setConfig((c) => ({
        ...c,
        measures: applyMeasureAliasPolicy(
          c.measures.filter((_, i) => i !== idx),
          aliasPolicyMode,
        ),
      }));
    },
    [aliasPolicyMode],
  );

  const addCuratedMeasure = useCallback(
    (catalogMeasure: CuratedMeasure) => {
      setConfig((c) => {
        const nextFilters = [...c.filters];
        for (const catalogFilter of catalogMeasure.filters || []) {
          nextFilters.push({ ...catalogFilter });
        }

        const baseAlias = catalogMeasure.label || catalogMeasure.key;
        const existingAliasCount = c.measures.filter((measure) =>
          measure.alias.startsWith(baseAlias),
        ).length;

        return {
          ...c,
          measures: applyMeasureAliasPolicy(
            [
              ...c.measures,
              {
                field_key: catalogMeasure.field_key,
                aggregation: catalogMeasure.aggregation as Measure['aggregation'],
                alias:
                  existingAliasCount === 0 ? baseAlias : `${baseAlias} ${existingAliasCount + 1}`,
              },
            ],
            aliasPolicyMode,
          ),
          filters: nextFilters,
        };
      });
    },
    [aliasPolicyMode],
  );

  const updateMeasureField = useCallback(
    (index: number, fieldKey: string) => {
      const semanticHint = semanticFieldByKey.get(fieldKey);
      const patch: Partial<Measure> = { field_key: fieldKey };

      if (isSupportedAggregation(semanticHint?.default_agg_hint)) {
        patch.aggregation = semanticHint.default_agg_hint;
      }

      if (!config.measures[index]?.alias) {
        const agg = patch.aggregation || config.measures[index]?.aggregation || 'count';
        patch.alias = `${agg}_${fieldKey}`;
      }

      updateMeasure(index, patch);
    },
    [config.measures, semanticFieldByKey, updateMeasure],
  );

  const toggleAliasPolicyMode = useCallback((mode: AliasPolicyMode) => {
    setAliasPolicyMode(mode);
    setConfig((c) => ({
      ...c,
      measures: applyMeasureAliasPolicy(c.measures || [], mode),
    }));
  }, []);

  const updateSlot = useCallback(
    (slotKey: VisualSlotDefinition['key'], fieldKey: string) => {
      if (slotKey === 'category' || slotKey === 'series') {
        setConfig((c) => {
          const currentPrimary = c.dimensions[0] || '';
          const currentSecondary = c.dimensions[1] || '';
          const nextPrimary = slotKey === 'category' ? fieldKey : currentPrimary;
          const nextSecondary = slotKey === 'series' ? fieldKey : currentSecondary;
          const nextDimensions: string[] = [];

          if (nextPrimary) {
            nextDimensions.push(nextPrimary);
            if (nextSecondary) nextDimensions.push(nextSecondary);
          }

          const nextConfig = applySlotConstraints({
            ...c,
            dimensions: nextDimensions,
          });

          if (slotKey !== 'category') {
            return nextConfig;
          }

          const nextGranularityMap = { ...(nextConfig.date_granularity || {}) };
          if (currentPrimary && currentPrimary !== nextPrimary) {
            delete nextGranularityMap[currentPrimary];
          }
          if (supportsTemporalAxis && temporalAxisMode === 'time' && nextPrimary) {
            nextGranularityMap[nextPrimary] = temporalGranularity;
          }

          return {
            ...nextConfig,
            date_granularity: Object.keys(nextGranularityMap).length
              ? nextGranularityMap
              : undefined,
          };
        });

        return;
      }

      if (slotKey === 'value' || slotKey === 'x_value') {
        setConfig((c) => {
          if (!fieldKey) {
            return applySlotConstraints({
              ...c,
              measures: [],
            });
          }

          const first = buildMeasure(fieldKey, 1, c.measures[0]);
          const existingSecond = c.measures[1];
          return applySlotConstraints({
            ...c,
            measures:
              widgetType === 'scatter' && existingSecond?.field_key
                ? [first, buildMeasure(existingSecond.field_key, 2, existingSecond)]
                : [first],
          });
        });

        return;
      }

      if (slotKey === 'y_value') {
        setConfig((c) => {
          const first = c.measures[0];
          if (!first?.field_key) return c;

          if (!fieldKey) {
            return applySlotConstraints({
              ...c,
              measures: [buildMeasure(first.field_key, 1, first)],
            });
          }

          return applySlotConstraints({
            ...c,
            measures: [
              buildMeasure(first.field_key, 1, first),
              buildMeasure(fieldKey, 2, c.measures[1]),
            ],
          });
        });
      }
    },
    [
      applySlotConstraints,
      buildMeasure,
      widgetType,
      supportsTemporalAxis,
      temporalAxisMode,
      temporalGranularity,
    ],
  );

  /* ---------- Filters ---------- */
  const addFilter = useCallback(() => {
    setConfig((c) => ({
      ...c,
      filters: [...c.filters, { scope: 'value', field_key: '', operator: '=' as const, value: '' }],
    }));
  }, []);
  const updateFilter = useCallback((idx: number, patch: Partial<FilterCondition>) => {
    setConfig((c) => ({
      ...c,
      filters: c.filters.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    }));
  }, []);
  const removeFilter = useCallback((idx: number) => {
    setConfig((c) => ({ ...c, filters: c.filters.filter((_, i) => i !== idx) }));
  }, []);

  /* ---------- Sort ---------- */
  const addSort = useCallback(() => {
    setConfig((c) => ({
      ...c,
      sort: [...c.sort, { field: '', direction: 'asc' as const }],
    }));
  }, []);
  const updateSort = useCallback((idx: number, patch: Partial<SortConfig>) => {
    setConfig((c) => ({
      ...c,
      sort: c.sort.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  }, []);
  const removeSort = useCallback((idx: number) => {
    setConfig((c) => ({ ...c, sort: c.sort.filter((_, i) => i !== idx) }));
  }, []);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>Query Designer</h3>
          <button className={styles.closeBtn} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {isLoading ? (
          <div className={styles.loadingMsg}>Loading fields…</div>
        ) : (
          <div className={styles.body}>
            {guidanceHints.length > 0 && (
              <div className={styles.guidanceBox}>
                {guidanceHints.map((hint) => (
                  <div key={hint} className={styles.guidanceItem}>
                    {hint}
                  </div>
                ))}
              </div>
            )}

            {catalogSummary && (
              <div className={styles.guidanceBox}>
                <div className={styles.guidanceItem}>
                  Catalog {catalogSummary.catalog_version}: {catalogSummary.entity_count} entities,{' '}
                  {catalogSummary.derived_field_count} derived dimensions,{' '}
                  {catalogSummary.curated_measure_count} curated measures
                </div>
              </div>
            )}

            <Section title="Visual slots">
              {missingRequiredSlots.length > 0 && (
                <div className={styles.inlineIssue}>
                  Complete required slots: {missingRequiredSlots.join(', ')}.
                </div>
              )}
              <div className={styles.slotGrid}>
                {visualSlots.map((slot) => {
                  const slotOptionLabel = (field: {
                    field_name: string;
                    category: string;
                    field_key: string;
                  }) => `${field.field_name} (${field.category})`;
                  const value =
                    slot.key === 'category'
                      ? primaryDimension
                      : slot.key === 'series'
                        ? secondaryDimension
                        : slot.key === 'value' || slot.key === 'x_value'
                          ? primaryMeasure
                          : secondaryMeasure;

                  const options =
                    slot.kind === 'dimension'
                      ? slot.key === 'category' &&
                        supportsTemporalAxis &&
                        temporalAxisMode === 'time'
                        ? temporalDimensionOptions
                        : dimensionFieldOptions
                      : measureFieldOptions;
                  const isDisabled =
                    slot.key === 'series'
                      ? !primaryDimension
                      : slot.key === 'y_value'
                        ? !primaryMeasure
                        : false;
                  const lockedReason =
                    slot.key === 'series' && !primaryDimension
                      ? 'Select Category axis first to unlock Series split.'
                      : slot.key === 'y_value' && !primaryMeasure
                        ? 'Select X value first to unlock Y value.'
                        : null;
                  const emptyReason =
                    !isDisabled && options.length === 0
                      ? `No compatible ${slot.kind === 'dimension' ? 'dimension' : 'measure'} fields found for this slot.`
                      : null;
                  const selectedOption = options.find((field) => field.field_key === value);
                  const selectedLabel = selectedOption ? slotOptionLabel(selectedOption) : '';
                  const slotSearchValue = slotSearchValues[slot.key] ?? selectedLabel;
                  const isSearchableSlot = true;
                  const listId = `slot-${slot.key}-options`;

                  const matchFieldOption = (raw: string) => {
                    const normalised = raw.trim().toLowerCase();
                    if (!normalised) return null;

                    return (
                      options.find(
                        (field) =>
                          field.field_key.toLowerCase() === normalised ||
                          slotOptionLabel(field).toLowerCase() === normalised,
                      ) || null
                    );
                  };

                  return (
                    <label key={slot.key} className={styles.slotItem}>
                      <span className={styles.slotLabel}>
                        {slot.label}
                        {slot.optional ? (
                          <span className={styles.slotOptional}>
                            {' '}
                            ({slot.helperText || 'Optional'})
                          </span>
                        ) : null}
                      </span>
                      {isSearchableSlot ? (
                        <>
                          <input
                            className={styles.select}
                            aria-label={`slot-${slot.key}`}
                            disabled={isDisabled}
                            list={listId}
                            placeholder={slot.optional ? '— none —' : 'Search and select a field'}
                            value={slotSearchValue}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setSlotSearchValues((prev) => ({ ...prev, [slot.key]: nextValue }));

                              if (!nextValue.trim()) {
                                updateSlot(slot.key, '');
                                return;
                              }

                              const matched = matchFieldOption(nextValue);
                              if (matched) {
                                updateSlot(slot.key, matched.field_key);
                              }
                            }}
                            onBlur={(e) => {
                              const matched = matchFieldOption(e.target.value);
                              setSlotSearchValues((prev) => ({
                                ...prev,
                                [slot.key]: matched ? slotOptionLabel(matched) : selectedLabel,
                              }));
                            }}
                          />
                          <datalist id={listId}>
                            {slot.optional && <option value="" />}
                            {options.map((field) => (
                              <option key={field.field_key} value={slotOptionLabel(field)} />
                            ))}
                          </datalist>
                        </>
                      ) : (
                        <select
                          className={styles.select}
                          value={value}
                          aria-label={`slot-${slot.key}`}
                          disabled={isDisabled}
                          onChange={(e) => updateSlot(slot.key, e.target.value)}
                        >
                          <option value="">
                            {slot.optional ? '— none —' : '— select field —'}
                          </option>
                          {options.map((field) => (
                            <option key={field.field_key} value={field.field_key}>
                              {field.field_name} ({field.category})
                            </option>
                          ))}
                        </select>
                      )}
                      {(slot.hintText || lockedReason || emptyReason) && (
                        <span className={styles.slotHint}>
                          {lockedReason || emptyReason || slot.hintText}
                        </span>
                      )}
                      {slot.kind === 'measure' && (
                        <div className={styles.row} style={{ marginTop: '0.35rem' }}>
                          <select
                            className={styles.selectSm}
                            aria-label={`slot-${slot.key}-aggregation`}
                            disabled={isDisabled || !value}
                            value={
                              config.measures[slot.key === 'y_value' ? 1 : 0]?.aggregation ||
                              'count'
                            }
                            onChange={(e) => {
                              const measureIdx = slot.key === 'y_value' ? 1 : 0;
                              updateMeasure(measureIdx, {
                                aggregation: e.target.value as Measure['aggregation'],
                              });
                            }}
                          >
                            {AGGREGATIONS.map((a) => (
                              <option key={a} value={a}>
                                {a.replace('_', ' ')}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {slot.key === 'category' && supportsTemporalAxis && (
                        <div className={styles.row} style={{ marginTop: '0.35rem' }}>
                          <select
                            className={styles.selectSm}
                            aria-label="slot-category-axis-mode"
                            value={temporalAxisMode}
                            onChange={(e) => {
                              const nextMode = e.target.value as TemporalAxisMode;
                              setTemporalAxisMode(nextMode);
                              const currentDimension = config.dimensions[0] || '';
                              if (!currentDimension || nextMode !== 'category') return;

                              setConfig((currentConfig) => {
                                const nextGranularityMap = {
                                  ...(currentConfig.date_granularity || {}),
                                };
                                delete nextGranularityMap[currentDimension];
                                return {
                                  ...currentConfig,
                                  date_granularity: Object.keys(nextGranularityMap).length
                                    ? nextGranularityMap
                                    : undefined,
                                };
                              });
                            }}
                          >
                            <option value="category">Categorical axis</option>
                            <option value="time">Time series axis</option>
                          </select>
                          {temporalAxisMode === 'time' && (
                            <select
                              className={styles.selectSm}
                              aria-label="slot-category-time-granularity"
                              value={temporalGranularity}
                              onChange={(e) =>
                                setTemporalGranularity(e.target.value as DateGranularity)
                              }
                            >
                              {TIME_GRANULARITY_OPTIONS.map((granularityOption) => (
                                <option key={granularityOption} value={granularityOption}>
                                  {granularityOption[0].toUpperCase() + granularityOption.slice(1)}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            </Section>

            <div className={styles.advancedModeRow}>
              <button
                type="button"
                className={styles.advancedToggle}
                aria-pressed={advancedModeEnabled}
                onClick={() => setAdvancedModeEnabled((value) => !value)}
              >
                {advancedModeEnabled ? 'Hide' : 'Show'} advanced query controls
              </button>
            </div>

            {/* Dimensions */}
            {advancedModeEnabled && (
              <Section title="Dimensions (Group by)">
                {derivedDimensions.length > 0 && (
                  <div className={styles.catalogBox}>
                    <div className={styles.catalogHeader}>Derived dimensions</div>
                    <div className={styles.catalogList}>
                      {derivedDimensions.map((dimension) => {
                        const alreadyAdded = config.dimensions.includes(dimension.key);
                        return (
                          <button
                            key={dimension.key}
                            type="button"
                            className={styles.catalogItem}
                            onClick={() => addDimension(dimension.key)}
                            disabled={alreadyAdded}
                            title={dimension.expression}
                          >
                            <span className={styles.catalogItemLabel}>{dimension.label}</span>
                            <span className={styles.catalogItemMeta}>{dimension.source_field}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {dimensionIssues.length > 0 && (
                  <div className={styles.inlineIssue}>
                    {dimensionIssues.map((issue, idx) => (
                      <div key={`${issue.code}-${idx}`}>
                        {issue.path ? `${issue.path}: ` : ''}
                        {issue.message}
                      </div>
                    ))}
                  </div>
                )}
                {config.dimensions.map((d, i) => {
                  const rowIssues = getIndexedIssues('roles.category_fields', i);
                  const fieldDef = availableFields.find((f) => f.field_key === d);
                  const isDate =
                    fieldDef?.field_type === 'date' ||
                    fieldDef?.field_type === 'timestamp' ||
                    fieldDef?.field_type === 'datetime';
                  const granularity = config.date_granularity?.[d];
                  return (
                    <div
                      key={i}
                      className={styles.rowWrap}
                      data-testid={`dimension-row-${i}`}
                      data-has-issue={rowIssues.length > 0 ? 'true' : 'false'}
                    >
                      <div
                        className={`${styles.row} ${rowIssues.length > 0 ? styles.rowIssue : ''}`}
                        style={{ flexWrap: 'wrap', gap: '0.35rem' }}
                      >
                        <span className={styles.chip}>{d}</span>
                        {isDate && (
                          <select
                            className={styles.selectSm}
                            value={granularity || ''}
                            onChange={(e) => {
                              const val = e.target.value as DateGranularity | '';
                              setConfig((c) => {
                                const g = { ...(c.date_granularity || {}) };
                                if (val) g[d] = val as DateGranularity;
                                else delete g[d];
                                return {
                                  ...c,
                                  date_granularity: Object.keys(g).length ? g : undefined,
                                };
                              });
                            }}
                          >
                            <option value="">No grouping</option>
                            <option value="day">Day</option>
                            <option value="week">Week</option>
                            <option value="month">Month</option>
                            <option value="quarter">Quarter</option>
                            <option value="year">Year</option>
                          </select>
                        )}
                        <button className={styles.removeBtn} onClick={() => removeDimension(i)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      {rowIssues.length > 0 && (
                        <div className={styles.rowIssueText}>
                          {rowIssues.map((issue, idx) => (
                            <div key={`${issue.code}-${idx}`}>{issue.message}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <>
                  <input
                    className={styles.select}
                    list="add-dimension-options"
                    aria-label="add-dimension"
                    placeholder="+ Add dimension"
                    onChange={(e) => {
                      const raw = e.target.value.trim().toLowerCase();
                      if (!raw) return;
                      const match = dimensionFieldOptions
                        .filter((f) => !config.dimensions.includes(f.field_key))
                        .find(
                          (f) =>
                            f.field_key.toLowerCase() === raw ||
                            `${f.field_name} (${f.category})`.toLowerCase() === raw,
                        );
                      if (match) {
                        addDimension(match.field_key);
                        e.target.value = '';
                      }
                    }}
                  />
                  <datalist id="add-dimension-options">
                    {dimensionFieldOptions
                      .filter((f) => !config.dimensions.includes(f.field_key))
                      .map((f) => (
                        <option key={f.field_key} value={`${f.field_name} (${f.category})`} />
                      ))}
                  </datalist>
                </>
              </Section>
            )}

            {/* Measures */}
            {advancedModeEnabled && (
              <Section title="Measures">
                {featuredMeasures.length > 0 && (
                  <div className={styles.catalogBox}>
                    <div className={styles.catalogHeader}>Featured measures</div>
                    <div className={styles.catalogList}>
                      {featuredMeasures.map((catalogMeasure) => {
                        const isDerived =
                          Array.isArray(catalogMeasure.derived_from) &&
                          catalogMeasure.derived_from.length > 0;
                        const widgetTypeAllowed =
                          !widgetType || catalogMeasure.valid_widget_types.includes(widgetType);
                        const disabled = isDerived || !widgetTypeAllowed;
                        return (
                          <button
                            key={catalogMeasure.key}
                            type="button"
                            className={styles.catalogItem}
                            onClick={() => addCuratedMeasure(catalogMeasure)}
                            disabled={disabled}
                            title={
                              isDerived
                                ? 'Derived measures are staged for a later slice'
                                : widgetTypeAllowed
                                  ? catalogMeasure.description
                                  : 'This measure is not valid for the current widget type'
                            }
                          >
                            <span className={styles.catalogItemLabel}>{catalogMeasure.label}</span>
                            <span className={styles.catalogItemMeta}>
                              {catalogMeasure.default_format}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {curatedMeasures.length > 0 && (
                  <div className={styles.catalogBox}>
                    <div className={styles.catalogHeader}>Curated measures</div>
                    <div className={styles.catalogList}>
                      {curatedMeasures.map((catalogMeasure) => {
                        const isDerived =
                          Array.isArray(catalogMeasure.derived_from) &&
                          catalogMeasure.derived_from.length > 0;
                        const widgetTypeAllowed =
                          !widgetType || catalogMeasure.valid_widget_types.includes(widgetType);
                        const disabled = isDerived || !widgetTypeAllowed;
                        return (
                          <button
                            key={catalogMeasure.key}
                            type="button"
                            className={styles.catalogItem}
                            onClick={() => addCuratedMeasure(catalogMeasure)}
                            disabled={disabled}
                            title={
                              isDerived
                                ? 'Derived measures are staged for a later slice'
                                : widgetTypeAllowed
                                  ? catalogMeasure.description
                                  : 'This measure is not valid for the current widget type'
                            }
                          >
                            <span className={styles.catalogItemLabel}>{catalogMeasure.label}</span>
                            <span className={styles.catalogItemMeta}>
                              {catalogMeasure.default_format}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {measureIssues.length > 0 && (
                  <div className={styles.inlineIssue}>
                    {measureIssues.map((issue, idx) => (
                      <div key={`${issue.code}-${idx}`}>
                        {issue.path ? `${issue.path}: ` : ''}
                        {issue.message}
                      </div>
                    ))}
                  </div>
                )}
                <div className={styles.aliasModeRow}>
                  <span className={styles.aliasModeLabel}>Alias mode</span>
                  <label className={styles.aliasModeOption}>
                    <input
                      type="radio"
                      name="alias-policy-mode"
                      checked={aliasPolicyMode === 'auto'}
                      onChange={() => toggleAliasPolicyMode('auto')}
                    />
                    Auto-manage
                  </label>
                  <label className={styles.aliasModeOption}>
                    <input
                      type="radio"
                      name="alias-policy-mode"
                      checked={aliasPolicyMode === 'manual'}
                      onChange={() => toggleAliasPolicyMode('manual')}
                    />
                    Manual
                  </label>
                </div>
                {config.measures.map((m, i) => {
                  const rowIssues = getIndexedIssues('roles.value_fields', i);
                  const selectedMeasure = measureFieldOptions.find(
                    (f) => f.field_key === m.field_key,
                  );
                  const selectedMeasureLabel = selectedMeasure
                    ? `${selectedMeasure.field_name} (${selectedMeasure.category})`
                    : '';
                  const measureSearchValue =
                    measureSearchValues[i] !== undefined
                      ? measureSearchValues[i]
                      : selectedMeasureLabel;
                  return (
                    <div
                      key={i}
                      className={styles.rowWrap}
                      data-testid={`measure-row-${i}`}
                      data-has-issue={rowIssues.length > 0 ? 'true' : 'false'}
                    >
                      <div
                        className={`${styles.measureRow} ${rowIssues.length > 0 ? styles.rowIssue : ''}`}
                      >
                        <select
                          className={styles.selectSm}
                          value={m.aggregation}
                          onChange={(e) =>
                            updateMeasure(i, {
                              aggregation: e.target.value as Measure['aggregation'],
                            })
                          }
                        >
                          {AGGREGATIONS.map((a) => (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          ))}
                        </select>
                        <>
                          <input
                            className={styles.selectSm}
                            list={`measure-field-options-${i}`}
                            aria-label={`measure-field-${i}`}
                            placeholder="— field —"
                            value={measureSearchValue}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setMeasureSearchValues((prev) => ({ ...prev, [i]: nextValue }));

                              const raw = nextValue.trim().toLowerCase();
                              if (!raw) {
                                updateMeasureField(i, '');
                                return;
                              }
                              const match = measureFieldOptions.find(
                                (f) =>
                                  f.field_key.toLowerCase() === raw ||
                                  f.field_name.toLowerCase() === raw ||
                                  `${f.field_name} (${f.category})`.toLowerCase() === raw,
                              );
                              if (match) {
                                updateMeasureField(i, match.field_key);
                              }
                            }}
                            onBlur={(e) => {
                              const raw = e.target.value.trim().toLowerCase();
                              const match = measureFieldOptions.find(
                                (f) =>
                                  f.field_key.toLowerCase() === raw ||
                                  f.field_name.toLowerCase() === raw ||
                                  `${f.field_name} (${f.category})`.toLowerCase() === raw,
                              );
                              setMeasureSearchValues((prev) => ({
                                ...prev,
                                [i]: match
                                  ? `${match.field_name} (${match.category})`
                                  : selectedMeasureLabel,
                              }));
                            }}
                          />
                          <datalist id={`measure-field-options-${i}`}>
                            {measureFieldOptions.map((f) => (
                              <option key={f.field_key} value={`${f.field_name} (${f.category})`} />
                            ))}
                          </datalist>
                        </>
                        <input
                          className={styles.input}
                          placeholder={aliasPolicyMode === 'auto' ? 'auto-managed' : 'alias'}
                          value={m.alias}
                          disabled={aliasPolicyMode === 'auto'}
                          onChange={(e) => updateMeasure(i, { alias: e.target.value })}
                        />
                        <button className={styles.removeBtn} onClick={() => removeMeasure(i)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      {rowIssues.length > 0 && (
                        <div className={styles.rowIssueText}>
                          {rowIssues.map((issue, idx) => (
                            <div key={`${issue.code}-${idx}`}>{issue.message}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <button className={styles.addBtn} onClick={addMeasure}>
                  <FontAwesomeIcon icon={faPlus} /> Add measure
                </button>
              </Section>
            )}

            {/* Filters */}
            <Section title="Filters" collapsed>
              {filterIssues.length > 0 && (
                <div className={styles.inlineIssue}>
                  {filterIssues.map((issue, idx) => (
                    <div key={`${issue.code}-${idx}`}>
                      {issue.path ? `${issue.path}: ` : ''}
                      {issue.message}
                    </div>
                  ))}
                </div>
              )}
              {config.filters.map((f, i) => {
                const rowIssues = getIndexedIssues('filters', i);
                const filterScope = resolveFilterScope(f.scope);
                const fieldDef = availableFields.find((fd) => fd.field_key === f.field_key);
                const baseValueKind = resolveFilterValueKind(
                  fieldDef?.field_type,
                  fieldDef?.db_type,
                );
                const valueKind = isAggregateFilterScope(filterScope) ? 'number' : baseValueKind;
                const valueInputType =
                  f.operator === 'like'
                    ? 'text'
                    : valueKind === 'number'
                      ? 'number'
                      : valueKind === 'date'
                        ? 'date'
                        : 'text';
                const operatorOptions = isAggregateFilterScope(filterScope)
                  ? getFilterOperatorsForScope(filterScope)
                  : getTypedFilterOperators(valueKind);
                const fieldOptions =
                  filterScope === 'sum_of' || filterScope === 'avg_of'
                    ? availableFields.filter(
                        (fd) => resolveFilterValueKind(fd.field_type, fd.db_type) === 'number',
                      )
                    : availableFields;
                const selectedFieldLabel = fieldDef
                  ? `${fieldDef.field_name} (${fieldDef.category})`
                  : '';
                const filterFieldSearchValue =
                  filterFieldSearchValues[i] !== undefined
                    ? filterFieldSearchValues[i]
                    : selectedFieldLabel;
                const optionValues = fieldDef?.options || [];
                return (
                  <div
                    key={i}
                    className={styles.rowWrap}
                    data-testid={`filter-row-${i}`}
                    data-has-issue={rowIssues.length > 0 ? 'true' : 'false'}
                  >
                    <div
                      className={`${styles.filterRow} ${rowIssues.length > 0 ? styles.rowIssue : ''}`}
                    >
                      <select
                        className={styles.selectSm}
                        value={filterScope}
                        onChange={(e) => {
                          const nextScope = resolveFilterScope(
                            e.target.value as FilterCondition['scope'],
                          );
                          const nextValueKind = isAggregateFilterScope(nextScope)
                            ? 'number'
                            : resolveFilterValueKind(fieldDef?.field_type, fieldDef?.db_type);
                          const nextOperatorOptions = isAggregateFilterScope(nextScope)
                            ? getFilterOperatorsForScope(nextScope)
                            : getTypedFilterOperators(nextValueKind);
                          const nextOperator = nextOperatorOptions.some(
                            (option) => option.value === f.operator,
                          )
                            ? f.operator
                            : nextOperatorOptions[0].value;
                          const currentFieldKind = fieldDef
                            ? resolveFilterValueKind(fieldDef.field_type, fieldDef.db_type)
                            : 'text';
                          const requiresNumericField =
                            nextScope === 'sum_of' || nextScope === 'avg_of';

                          updateFilter(i, {
                            scope: nextScope,
                            operator: nextOperator,
                            field_key:
                              requiresNumericField && currentFieldKind !== 'number'
                                ? ''
                                : f.field_key,
                            value: normaliseFilterValue(nextOperator, f.value, nextValueKind),
                          });
                        }}
                      >
                        {FILTER_SCOPE_OPTIONS.map((scopeOption) => (
                          <option key={scopeOption.value} value={scopeOption.value}>
                            {scopeOption.label}
                          </option>
                        ))}
                      </select>
                      <>
                        <input
                          className={styles.selectSm}
                          list={`filter-field-options-${i}`}
                          aria-label={`filter-field-${i}`}
                          placeholder="— field —"
                          value={filterFieldSearchValue}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setFilterFieldSearchValues((prev) => ({ ...prev, [i]: nextValue }));

                            const raw = nextValue.trim().toLowerCase();
                            if (!raw) {
                              updateFilter(i, { field_key: '' });
                              return;
                            }

                            const match = fieldOptions.find(
                              (fd) =>
                                fd.field_key.toLowerCase() === raw ||
                                fd.field_name.toLowerCase() === raw ||
                                `${fd.field_name} (${fd.category})`.toLowerCase() === raw,
                            );

                            if (!match) return;

                            const matchedKind = resolveFilterValueKind(
                              match.field_type,
                              match.db_type,
                            );
                            const nextOps = isAggregateFilterScope(filterScope)
                              ? getFilterOperatorsForScope(filterScope)
                              : getTypedFilterOperators(matchedKind);
                            const nextOperator = nextOps.some((op) => op.value === f.operator)
                              ? f.operator
                              : nextOps[0].value;

                            updateFilter(i, {
                              field_key: match.field_key,
                              operator: nextOperator,
                              value: normaliseFilterValue(nextOperator, f.value, matchedKind),
                            });
                          }}
                          onBlur={(e) => {
                            const raw = e.target.value.trim().toLowerCase();
                            const match = fieldOptions.find(
                              (fd) =>
                                fd.field_key.toLowerCase() === raw ||
                                fd.field_name.toLowerCase() === raw ||
                                `${fd.field_name} (${fd.category})`.toLowerCase() === raw,
                            );
                            setFilterFieldSearchValues((prev) => ({
                              ...prev,
                              [i]: match
                                ? `${match.field_name} (${match.category})`
                                : selectedFieldLabel,
                            }));
                          }}
                        />
                        <datalist id={`filter-field-options-${i}`}>
                          {fieldOptions.map((fd) => (
                            <option
                              key={fd.field_key}
                              value={`${fd.field_name} (${fd.category})`}
                            />
                          ))}
                        </datalist>
                      </>
                      <select
                        className={styles.selectSm}
                        value={f.operator}
                        onChange={(e) =>
                          updateFilter(i, {
                            operator: e.target.value as FilterCondition['operator'],
                            value: normaliseFilterValue(
                              e.target.value as FilterCondition['operator'],
                              f.value,
                              valueKind,
                            ),
                          })
                        }
                      >
                        {operatorOptions.map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </select>
                      {valueKind === 'option' &&
                      f.operator !== 'is_null' &&
                      f.operator !== 'is_not_null' ? (
                        <select
                          className={styles.selectSm}
                          value={String(f.value ?? '')}
                          onChange={(e) =>
                            updateFilter(i, {
                              value: e.target.value,
                            })
                          }
                        >
                          <option value="">— select option —</option>
                          {optionValues.map((opt) => (
                            <option key={`${opt.value}-${opt.label}`} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : f.operator === 'between' ? (
                        <div style={{ display: 'flex', gap: '0.25rem', flex: 1, minWidth: 0 }}>
                          <input
                            type={valueInputType}
                            className={styles.input}
                            placeholder="from"
                            value={Array.isArray(f.value) ? String(f.value[0] ?? '') : ''}
                            onChange={(e) =>
                              updateFilter(i, {
                                value: [
                                  parseScalarFilterValue(e.target.value, valueKind),
                                  Array.isArray(f.value)
                                    ? parseScalarFilterValue(String(f.value[1] ?? ''), valueKind)
                                    : '',
                                ],
                              })
                            }
                          />
                          <input
                            type={valueInputType}
                            className={styles.input}
                            placeholder="to"
                            value={Array.isArray(f.value) ? String(f.value[1] ?? '') : ''}
                            onChange={(e) =>
                              updateFilter(i, {
                                value: [
                                  Array.isArray(f.value)
                                    ? parseScalarFilterValue(String(f.value[0] ?? ''), valueKind)
                                    : '',
                                  parseScalarFilterValue(e.target.value, valueKind),
                                ],
                              })
                            }
                          />
                        </div>
                      ) : f.operator === 'in' || f.operator === 'not_in' ? (
                        <input
                          type="text"
                          className={styles.input}
                          placeholder={
                            valueKind === 'date' ? 'YYYY-MM-DD, YYYY-MM-DD' : 'value1, value2'
                          }
                          value={formatDelimitedValue(f.value)}
                          onChange={(e) =>
                            updateFilter(i, {
                              value: e.target.value
                                .split(',')
                                .map((value) => parseScalarFilterValue(value.trim(), valueKind))
                                .filter((value) => value !== ''),
                            })
                          }
                        />
                      ) : f.operator !== 'is_null' && f.operator !== 'is_not_null' ? (
                        <input
                          type={valueInputType}
                          className={styles.input}
                          placeholder={valueKind === 'date' ? 'YYYY-MM-DD' : 'value'}
                          value={String(f.value ?? '')}
                          onChange={(e) =>
                            updateFilter(i, {
                              value: parseScalarFilterValue(e.target.value, valueKind),
                            })
                          }
                        />
                      ) : null}
                      <button className={styles.removeBtn} onClick={() => removeFilter(i)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                    {rowIssues.length > 0 && (
                      <div className={styles.rowIssueText}>
                        {rowIssues.map((issue, idx) => (
                          <div key={`${issue.code}-${idx}`}>{issue.message}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <button className={styles.addBtn} onClick={addFilter}>
                <FontAwesomeIcon icon={faPlus} /> Add filter
              </button>
            </Section>

            {/* Sort */}
            <Section title="Sort" collapsed>
              {sortIssues.length > 0 && (
                <div className={styles.inlineIssue}>
                  {sortIssues.map((issue, idx) => (
                    <div key={`${issue.code}-${idx}`}>
                      {issue.path ? `${issue.path}: ` : ''}
                      {issue.message}
                    </div>
                  ))}
                </div>
              )}
              {config.sort.map((s, i) => {
                const rowIssues = getIndexedIssues('sorting', i);
                return (
                  <div
                    key={i}
                    className={styles.rowWrap}
                    data-testid={`sort-row-${i}`}
                    data-has-issue={rowIssues.length > 0 ? 'true' : 'false'}
                  >
                    <div className={`${styles.row} ${rowIssues.length > 0 ? styles.rowIssue : ''}`}>
                      <select
                        className={styles.selectSm}
                        value={s.field}
                        onChange={(e) => updateSort(i, { field: e.target.value })}
                      >
                        <option value="">— field —</option>
                        {sortFieldOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <select
                        className={styles.selectSm}
                        value={s.direction}
                        onChange={(e) =>
                          updateSort(i, { direction: e.target.value as 'asc' | 'desc' })
                        }
                      >
                        <option value="asc">Ascending</option>
                        <option value="desc">Descending</option>
                      </select>
                      <button className={styles.removeBtn} onClick={() => removeSort(i)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                    {rowIssues.length > 0 && (
                      <div className={styles.rowIssueText}>
                        {rowIssues.map((issue, idx) => (
                          <div key={`${issue.code}-${idx}`}>{issue.message}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <button className={styles.addBtn} onClick={addSort}>
                <FontAwesomeIcon icon={faPlus} /> Add sort
              </button>
            </Section>

            {/* Limit */}
            <Section title="Limit">
              <input
                type="number"
                className={styles.input}
                value={config.limit}
                min={1}
                max={10000}
                onChange={(e) =>
                  setConfig((c) => ({
                    ...c,
                    limit: Math.min(10000, Math.max(1, Number(e.target.value) || 1000)),
                  }))
                }
              />
            </Section>

            {/* Comparative Date */}
            <Section title="Compare Period" collapsed>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label className={styles.row} style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config.comparative_date?.enabled || false}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        comparative_date: {
                          enabled: e.target.checked,
                          compare_to: c.comparative_date?.compare_to || 'previous_period',
                        },
                      }))
                    }
                  />
                  <span style={{ fontSize: '0.85rem' }}>Enable period comparison</span>
                </label>
                {config.comparative_date?.enabled && (
                  <select
                    className={styles.select}
                    value={config.comparative_date.compare_to}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        comparative_date: {
                          enabled: true,
                          compare_to: e.target.value as 'previous_period' | 'same_period_last_year',
                        },
                      }))
                    }
                  >
                    <option value="previous_period">Previous period</option>
                    <option value="same_period_last_year">Same period last year</option>
                  </select>
                )}
              </div>
            </Section>

            {/* Projection */}
            {supportsProjection && (
              <Section title="Projection" collapsed>
                {projectionIssues.length > 0 && (
                  <div className={styles.inlineIssue}>
                    {projectionIssues.map((issue, idx) => (
                      <div key={`${issue.code}-${idx}`}>
                        {issue.path ? `${issue.path}: ` : ''}
                        {issue.message}
                      </div>
                    ))}
                  </div>
                )}
                <ProjectionConfigEditor value={projectionCfg} onChange={setProjectionCfg} />
              </Section>
            )}

            {/* Annotations */}
            {supportsAnnotations && (
              <Section title="Annotations" collapsed>
                <AnnotationEditor value={annotationsList} onChange={setAnnotationsList} />
              </Section>
            )}

            {/* Conditional Formatting */}
            <Section title="Conditional Formatting" collapsed>
              <ConditionalFormatRules value={formatRules} onChange={setFormatRules} />
            </Section>

            {/* Drill-Down */}
            <Section title="Drill-Down" collapsed>
              <DrilldownConfigEditor
                value={drilldownCfg}
                onChange={setDrilldownCfg}
                dimensions={config.dimensions}
              />
            </Section>

            {/* Value */}
            <Section title="Value" collapsed>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div className={styles.row} style={{ alignItems: 'center' }}>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, minWidth: '6rem' }}>
                    Format
                  </span>
                  <select
                    className={styles.selectSm}
                    value={(styleCfg.format as 'number' | 'currency' | 'percentage') || 'number'}
                    onChange={(e) =>
                      setStyleCfg((s) => ({
                        ...s,
                        format: e.target.value as 'number' | 'currency' | 'percentage',
                      }))
                    }
                  >
                    <option value="number">Number</option>
                    <option value="currency">Currency</option>
                    <option value="percentage">Percentage</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: '0.25rem',
                      }}
                    >
                      Prefix
                    </span>
                    <input
                      className={styles.input}
                      value={String(styleCfg.value_prefix || '')}
                      placeholder="e.g. £"
                      onChange={(e) =>
                        setStyleCfg((s) => ({
                          ...s,
                          value_prefix: e.target.value || undefined,
                        }))
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: '0.25rem',
                      }}
                    >
                      Postfix
                    </span>
                    <input
                      className={styles.input}
                      value={String(styleCfg.value_postfix || '')}
                      placeholder="e.g. %"
                      onChange={(e) =>
                        setStyleCfg((s) => ({
                          ...s,
                          value_postfix: e.target.value || undefined,
                        }))
                      }
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: '0.25rem',
                      }}
                    >
                      Unit
                    </span>
                    <input
                      className={styles.input}
                      value={String(styleCfg.value_unit || '')}
                      placeholder="e.g. clients"
                      onChange={(e) =>
                        setStyleCfg((s) => ({
                          ...s,
                          value_unit: e.target.value || undefined,
                        }))
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: '0.25rem',
                      }}
                    >
                      Decimal places
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={6}
                      className={styles.input}
                      value={
                        typeof styleCfg.value_decimal_places === 'number'
                          ? styleCfg.value_decimal_places
                          : ''
                      }
                      placeholder="Auto"
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (!raw) {
                          setStyleCfg((s) => ({ ...s, value_decimal_places: undefined }));
                          return;
                        }
                        const parsed = Math.max(0, Math.min(6, Number(raw) || 0));
                        setStyleCfg((s) => ({ ...s, value_decimal_places: parsed }));
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: '0.25rem',
                      }}
                    >
                      Thousands separator
                    </span>
                    <select
                      className={styles.select}
                      value={styleCfg.value_thousands_separator || ','}
                      onChange={(e) =>
                        setStyleCfg((s) => ({
                          ...s,
                          value_thousands_separator: e.target.value as ',' | '.' | ' ' | 'none',
                        }))
                      }
                    >
                      <option value=",">Comma (,)</option>
                      <option value=".">Dot (.)</option>
                      <option value=" ">Space</option>
                      <option value="none">None</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: '0.25rem',
                      }}
                    >
                      Decimal separator
                    </span>
                    <select
                      className={styles.select}
                      value={styleCfg.value_decimal_separator || '.'}
                      onChange={(e) =>
                        setStyleCfg((s) => ({
                          ...s,
                          value_decimal_separator: e.target.value as '.' | ',',
                        }))
                      }
                    >
                      <option value=".">Dot (.)</option>
                      <option value=",">Comma (,)</option>
                    </select>
                  </div>
                </div>
              </div>
            </Section>

            {/* Visual Style */}
            <Section title="Visual Style" collapsed>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {/* Legend position */}
                {supportsLegend && (
                  <div className={styles.row} style={{ alignItems: 'center' }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, minWidth: '6rem' }}>
                      Legend
                    </span>
                    <select
                      className={styles.selectSm}
                      value={styleCfg.legend_position || 'bottom'}
                      onChange={(e) =>
                        setStyleCfg((s) => ({
                          ...s,
                          legend_position: e.target.value as StyleConfig['legend_position'],
                        }))
                      }
                    >
                      <option value="top">Top</option>
                      <option value="bottom">Bottom</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                      <option value="none">Hidden</option>
                    </select>
                  </div>
                )}

                {/* Data labels */}
                {supportsDataLabels && (
                  <label className={styles.row} style={{ cursor: 'pointer', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={styleCfg.show_data_labels ?? true}
                      onChange={(e) =>
                        setStyleCfg((s) => ({ ...s, show_data_labels: e.target.checked }))
                      }
                    />
                    <span style={{ fontSize: '0.8125rem' }}>Show data labels</span>
                  </label>
                )}

                {supportsDataLabels && (styleCfg.show_data_labels ?? true) && (
                  <>
                    <div className={styles.row} style={{ alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 600, minWidth: '6rem' }}>
                        Label shows
                      </span>
                      <select
                        className={styles.selectSm}
                        value={styleCfg.label_content || 'name_pct'}
                        onChange={(e) =>
                          setStyleCfg((s) => ({
                            ...s,
                            label_content: e.target.value as StyleConfig['label_content'],
                          }))
                        }
                      >
                        <option value="name">Name only</option>
                        <option value="value">Value only</option>
                        <option value="percentage">Percentage only</option>
                        <option value="name_value">Name + Value</option>
                        <option value="name_pct">Name + %</option>
                        <option value="value_pct">Value + %</option>
                        <option value="all">Name + Value + %</option>
                      </select>
                    </div>

                    <div className={styles.row} style={{ alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 600, minWidth: '6rem' }}>
                        Label position
                      </span>
                      <select
                        className={styles.selectSm}
                        value={styleCfg.label_position || 'outside'}
                        onChange={(e) =>
                          setStyleCfg((s) => ({
                            ...s,
                            label_position: e.target.value as StyleConfig['label_position'],
                          }))
                        }
                      >
                        <option value="outside">Outside</option>
                        <option value="inside">Inside</option>
                        <option value="top">Top</option>
                        <option value="center">Centre</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Tooltip trigger */}
                {supportsTooltip && (
                  <div className={styles.row} style={{ alignItems: 'center' }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, minWidth: '6rem' }}>
                      Tooltip
                    </span>
                    <select
                      className={styles.selectSm}
                      value={styleCfg.tooltip_trigger || 'item'}
                      onChange={(e) =>
                        setStyleCfg((s) => ({
                          ...s,
                          tooltip_trigger: e.target.value as StyleConfig['tooltip_trigger'],
                        }))
                      }
                    >
                      <option value="item">On hover (item)</option>
                      <option value="axis">On hover (axis)</option>
                      <option value="none">Disabled</option>
                    </select>
                  </div>
                )}

                {/* Grid lines */}
                {supportsGridLines && (
                  <label className={styles.row} style={{ cursor: 'pointer', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={styleCfg.show_grid_lines ?? true}
                      onChange={(e) =>
                        setStyleCfg((s) => ({ ...s, show_grid_lines: e.target.checked }))
                      }
                    />
                    <span style={{ fontSize: '0.8125rem' }}>Show grid lines</span>
                  </label>
                )}

                {/* Data zoom */}
                {supportsDataZoom && (
                  <label className={styles.row} style={{ cursor: 'pointer', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={styleCfg.enable_data_zoom ?? false}
                      onChange={(e) =>
                        setStyleCfg((s) => ({ ...s, enable_data_zoom: e.target.checked }))
                      }
                    />
                    <span style={{ fontSize: '0.8125rem' }}>Enable scroll zoom</span>
                  </label>
                )}

                {/* Axis labels */}
                {supportsAxisLabels && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ flex: 1 }}>
                      <span
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          display: 'block',
                          marginBottom: '0.25rem',
                        }}
                      >
                        X-axis label
                      </span>
                      <input
                        className={styles.input}
                        value={styleCfg.axis_labels?.x || ''}
                        placeholder="Auto"
                        onChange={(e) =>
                          setStyleCfg((s) => ({
                            ...s,
                            axis_labels: { ...s.axis_labels, x: e.target.value || undefined },
                          }))
                        }
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <span
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          display: 'block',
                          marginBottom: '0.25rem',
                        }}
                      >
                        Y-axis label
                      </span>
                      <input
                        className={styles.input}
                        value={styleCfg.axis_labels?.y || ''}
                        placeholder="Auto"
                        onChange={(e) =>
                          setStyleCfg((s) => ({
                            ...s,
                            axis_labels: { ...s.axis_labels, y: e.target.value || undefined },
                          }))
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </Section>
          </div>
        )}

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button className={styles.saveBtn} onClick={handleSave}>
            <FontAwesomeIcon icon={faCheck} /> Apply
          </button>
        </div>
      </div>
    </div>
  );
}

/* Collapsible section helper */
function Section({
  title,
  collapsed: collapsedDefault = false,
  children,
}: {
  title: string;
  collapsed?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(!collapsedDefault);
  return (
    <div className={styles.section}>
      <button className={styles.sectionToggle} onClick={() => setOpen(!open)}>
        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
        <span>{title}</span>
      </button>
      {open && <div className={styles.sectionBody}>{children}</div>}
    </div>
  );
}
